import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { FiLock, FiLoader, FiCheck, FiAlertTriangle, FiEye, FiHome, FiGlobe, FiFileText, FiAlertCircle, FiUsers } from "react-icons/fi";
import { GoLaw } from "react-icons/go";
import { GiHammerNails } from "react-icons/gi";
import { LuBuilding2, LuRuler, LuShieldCheck } from "react-icons/lu";
import { ReactElement } from "react";
import { BRAND_GREEN } from "@dd-report-components";
import type { DueDiligenceTier } from "../../../../Common/Types-DTOs/DueDiligence";

// Case-level header info the sidebar shows above "Cobertura del informe"
// (tier/level, property title, address) — emitted by the report page via
// the REPORT_CASE_META event, mirrors the REPORT_OUTLINE pattern.
export interface ReportCaseMeta {
  tier: DueDiligenceTier | null;
  title: string;
  address: string;
}

export const SectionDataStatuses = {
  pending: "pending",
  partial: "partial",
  completed: "completed",
  blocked: "blocked",
  in_progress: "in_progress",
} as const;

export type SectionDataStatus = (typeof SectionDataStatuses)[keyof typeof SectionDataStatuses];

export interface ReportSectionDef {
  status?: SectionDataStatus;
  requiredLevel?: number;
  navTitle?: string;
  verdict?: SectionStatus;
  showVerdict?: boolean;

  id: string;
  number: number;
  displayNumber: number;
  title: string;
  icon: ReactElement;
  subsections: number;
}

// Outline shape — matches what the future GET /report/outline endpoint will
// return, and what the page emits today through the REPORT_OUTLINE event.
// The sidebar renders 100% from this; numbers and titles live in the backend
// mock as the single source of truth.
export interface OutlineBlock {
  id: string;     // "block-1-1"
  number: string; // "1.1"
  title: string;
}

export interface OutlineSection {
  status?: SectionDataStatus;
  requiredLevel?: number;
  navTitle?: string;
  verdict?: SectionStatus;
  showVerdict?: boolean;

  id: string;     // "section-1"
  number: number; // 1
  displayNumber: number; // what the reader sees — correlative per tier, e.g. 7
  title: string;
  blocks: OutlineBlock[];
}

export type ReportOutline = OutlineSection[];

const STATUSES_AWAITING_DATA = new Set<SectionDataStatus>([
  SectionDataStatuses.blocked,
  SectionDataStatuses.in_progress,
  SectionDataStatuses.pending,
]);

export const isSectionAwaitingData = (status?: SectionDataStatus): boolean =>
  !!status && STATUSES_AWAITING_DATA.has(status);

// Icons can't ride on the outline event (they're React elements, not JSON-
// safe), so we keep a static map keyed by section number. If the order or
// count of the report's sections changes, only this map needs an entry —
// everything else is derived from the outline.
export const LEGAL_FISCAL_SECTION_ICONS: Record<number, ReactElement> = {
  1: <FiEye size={14} />,
  2: <FiFileText size={14} />,
  3: <FiHome size={14} />,
  4: <FiUsers size={14} />,
  5: <GoLaw size={14} />,
  6: <LuRuler size={14} />,
  7: <LuBuilding2 size={14} />,
  8: <FiAlertCircle size={14} />,
  9: <FiGlobe size={14} />,
  10: <LuShieldCheck size={14} />,
  11: <GiHammerNails size={14} />,
  12: <FiFileText size={14} />,
  13: <FiFileText size={14} />,
};


// Sections whose figures are estimates rather than verified facts (MIKE-322,
// mirrors the design's own economico/acciones -> "Orientativo" rule). Every
// other section reads as "Verificado".
export const ORIENTATIVE_SECTION_NUMBERS = new Set([7, 11]);

// The source appendix and disclaimer have no status badge in the design. The
// synth strip is still rendered for them, like every other numbered section.
export const NO_STATUS_SECTION_NUMBERS = new Set([12, 13]);

// Most fixture titles are already the design's navigation labels. Keep only
// deliberate header-vs-nav differences here so the two contexts can both
// match the handoff.
const SECTION_NAV_NAME_OVERRIDES: Record<number, string> = {
  // The design intentionally uses the shorter name in the sidebar while the
  // section header keeps the fuller title.
  2: "Datos catastrales",
};

export const sectionNavName = (number: number, title: string): string =>
  SECTION_NAV_NAME_OVERRIDES[number] ?? title;

// Project an outline into the ReportSectionDef shape the nav components
// already consume. This is the ONE place where icons get attached.
export const sectionsFromOutline = (outline: ReportOutline): ReportSectionDef[] =>
  outline.map((s) => ({
    status: s.status, requiredLevel: s.requiredLevel, navTitle: s.navTitle, verdict: s.verdict, showVerdict: s.showVerdict,
    id: s.id,
    number: s.number,
    displayNumber: s.displayNumber,
    title: s.title,
    icon: LEGAL_FISCAL_SECTION_ICONS[s.number] || <FiFileText size={14} />,
    subsections: s.blocks.length,
  }));

// Project an outline into the per-section blocks map the nav uses for the
// "(n/total)" counter and the expand-to-show-subsections panel.
export const blocksOutlineFromOutline = (outline: ReportOutline): Record<string, OutlineBlock[]> => {
  const map: Record<string, OutlineBlock[]> = {};
  outline.forEach((s) => {
    map[s.id] = s.blocks;
  });
  return map;
};

// Back-compat re-exports under the older names, so existing imports keep
// compiling. The new code should import OutlineBlock / ReportOutline.
export type ReportSubsectionDef = OutlineBlock;
export type BlocksOutline = Record<string, OutlineBlock[]>;

// ── Shared toggle (iOS style) ────────────────────────────────────

const spin = keyframes`to { transform: rotate(360deg); }`;
const ProgressIcon = styled(FiLoader)`animation: ${spin} 1s linear infinite;`;
const Availability = ({ section }: { section: ReportSectionDef }) => {
  const { t } = useTranslation();
  const label = (key: string, options = {}) =>
    t(`dueDiligence.legalFiscalReport.reportSectionNav.availability.${key}`, options);
  return section.status === SectionDataStatuses.blocked
    ? <><FiLock size={11} /> {label("locked", { level: section.requiredLevel })}</>
    : <><ProgressIcon size={11} aria-label={label("inProgress")} /> {label("inProgress")}</>;
};

const Toggle = styled.div<{ $isOn: boolean }>`
  width: 32px;
  height: 18px;
  border-radius: 9px;
  background-color: ${(p) => (p.$isOn ? "#16a34a" : "#4b5563")};
  position: relative;
  transition: background-color 0.2s;
  flex-shrink: 0;
  cursor: pointer;

  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: #ffffff;
    top: 2px;
    left: ${(p) => (p.$isOn ? "16px" : "2px")};
    transition: left 0.2s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

// ── Desktop sidebar variant ──────────────────────────────────────

// Flex column so only SectionList (below) scrolls internally — everything
// above it here (case header, coverage bar, executive-summary CTA, the
// "Secciones del informe" label) stays put, same as the logo/user elsewhere
// in the sidebar.
const NavWrapper = styled.div`
  margin-top: 0.6rem;
  padding: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const CaseHeader = styled.div`
  padding: 0 0.35rem;
  margin-bottom: 0.9rem;
`;

const CaseLevelLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
`;

const CaseTitle = styled.div`
  margin-top: 0.3rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CaseAddress = styled.div`
  margin-top: 0.15rem;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CoverageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);
  padding: 0 0.35rem 0.45rem;
  font-weight: 600;
`;

const CoverageFraction = styled.span`
  font-weight: 700;
  color: #ffffff;
`;

const CoverageBarTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin: 0 0.35rem 0.9rem;
`;

const CoverageBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${BRAND_GREEN};
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const SectionsLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  padding: 0 0.45rem 0.4rem;
`;

// The executive summary is a standalone CTA above the numbered section list
// (matches the design's dedicated "Resumen ejecutivo" row) — it never shows
// the per-section status tile/badge, just a plain accent tile.
const ExecutiveSummaryItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.45rem;
  margin-bottom: 0.6rem;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.72rem;
  color: #ffffff;
  background: ${(p) => (p.$isActive ? "rgba(169, 253, 201, 0.16)" : "rgba(169, 253, 201, 0.08)")};
  transition: background 0.12s;

  &:hover {
    background: rgba(169, 253, 201, 0.16);
  }
`;

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const SectionItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.45rem;
  border-radius: 9px;
  cursor: pointer;
  font-size: 0.72rem;
  color: ${(p) => (p.$isActive ? "#ffffff" : "rgba(255, 255, 255, 0.65)")};
  background: ${(p) => (p.$isActive ? "rgba(255, 255, 255, 0.1)" : "transparent")};
  transition: background 0.12s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }
`;

// Design/demo verdicts shared by the sidebar and section header. Replace this
// map with report verdict data when the manifest exposes it; generation
// readiness (completed/pending) is a separate status.
export type SectionStatus = "favorable" | "reservas" | "critico";

const DESIGN_SECTION_STATUS: Record<number, SectionStatus> = {
  2: "favorable",
  3: "reservas",
  4: "reservas",
  5: "reservas",
  6: "favorable",
  7: "reservas",
  8: "favorable",
  9: "reservas",
  10: "reservas",
  11: "reservas",
};

export const designSectionStatus = (number: number): SectionStatus =>
  DESIGN_SECTION_STATUS[number] ?? "favorable";

const STATUS_STYLES: Record<SectionStatus, { bg: string; color: string }> = {
  favorable: { bg: "rgba(169, 253, 201, 0.16)", color: BRAND_GREEN },
  reservas: { bg: "rgba(245, 158, 11, 0.18)", color: "#F59E0B" },
  critico: { bg: "rgba(220, 38, 38, 0.18)", color: "#F87171" },
};

const SectionIconTile = styled.span<{ $status: SectionStatus; $enabled: boolean }>`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$enabled ? STATUS_STYLES[p.$status as SectionStatus].bg : "rgba(255, 255, 255, 0.06)")};
  color: ${(p) => (p.$enabled ? STATUS_STYLES[p.$status as SectionStatus].color : "rgba(255, 255, 255, 0.35)")};

  svg {
    flex-shrink: 0;
  }
`;

const SectionItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const SectionMeta = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const SectionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionCaption = styled.span`
  font-size: 0.62rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionStatusCaption = styled(SectionCaption)<{ $status: SectionStatus; $enabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(p) => p.$enabled ? STATUS_STYLES[p.$status as SectionStatus].color : "rgba(255, 255, 255, 0.35)"};

  svg {
    flex-shrink: 0;
  }
`;

const SectionNumber = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`;

interface DesktopProps {
  activeSectionId: string;
  activeBlockId?: string;
  enabledSections: Record<string, boolean>;
  enabledBlocks?: Record<string, boolean>;
  blocksOutline?: BlocksOutline;
  onSectionClick: (sectionId: string) => void;
  onToggleSection: (sectionId: string) => void;
  onBlockClick?: (blockId: string) => void;
  onToggleBlock?: (blockId: string) => void;
  sections?: ReportSectionDef[];
  caseMeta?: ReportCaseMeta | null;
}

export const ReportSectionNavDesktop = ({
  activeSectionId,
  enabledSections,
  onSectionClick,
  onToggleSection,
  sections = [],
  caseMeta = null,
}: DesktopProps) => {
  const { t } = useTranslation();
  const coverableSections = sections.filter((s) => s.status !== SectionDataStatuses.blocked);
  const activeCount = coverableSections.filter((s) => enabledSections[s.id] !== false && !isSectionAwaitingData(s.status)).length;
  const coveragePct = coverableSections.length > 0 ? Math.round((activeCount / coverableSections.length) * 100) : 0;

  // The executive summary, when included in the client preview, is rendered
  // as its own CTA above the numbered list, per the design — not as row #1
  // of "Secciones del informe".
  const executiveSummary = sections.find((section) => Number(section.number) === 1);
  const restSections = sections.filter((section) => Number(section.number) !== 1);

  return (
    <NavWrapper>
      {caseMeta && (
        <CaseHeader>
          {caseMeta.tier && (
            <CaseLevelLabel>{t(`dueDiligence.caseDetails.tierLabels.${caseMeta.tier}`)}</CaseLevelLabel>
          )}
          {caseMeta.title && <CaseTitle title={caseMeta.title}>{caseMeta.title}</CaseTitle>}
          {caseMeta.address && <CaseAddress title={caseMeta.address}>{caseMeta.address}</CaseAddress>}
        </CaseHeader>
      )}
      <CoverageHeader>
        <span>{t("dueDiligence.legalFiscalReport.reportSectionNav.coverageLabel")}</span>
        <CoverageFraction>{activeCount}/{coverableSections.length}</CoverageFraction>
      </CoverageHeader>
      <CoverageBarTrack>
        <CoverageBarFill $pct={coveragePct} />
      </CoverageBarTrack>
      {executiveSummary && (
        <ExecutiveSummaryItem
          $isActive={activeSectionId === executiveSummary.id}
          onClick={() => enabledSections[executiveSummary.id] !== false && onSectionClick(executiveSummary.id)}
        >
          <SectionIconTile $status="favorable" $enabled>
            {executiveSummary.icon}
          </SectionIconTile>
          <SectionMeta>
            <SectionName>{executiveSummary.title}</SectionName>
            <SectionCaption>{t("dueDiligence.legalFiscalReport.reportSectionNav.executiveSummaryCaption")}</SectionCaption>
          </SectionMeta>
        </ExecutiveSummaryItem>
      )}
      <SectionsLabel>{t("dueDiligence.legalFiscalReport.reportSectionNav.sectionsHeader")}</SectionsLabel>
      <SectionList>
        {restSections.map((s) => {
          const enabled = enabledSections[s.id] !== false;
          const isActive = activeSectionId === s.id;
          const status = s.verdict ?? designSectionStatus(s.number);
          const unavailable = isSectionAwaitingData(s.status);
          const StatusIcon = status === "favorable" ? FiCheck : status === "reservas" ? FiAlertTriangle : FiAlertCircle;
          return (
            <SectionItem key={s.id} $isActive={isActive}>
              <SectionItemLeft onClick={() => enabled && onSectionClick(s.id)} title={`${s.displayNumber}. ${s.navTitle ?? sectionNavName(s.number, s.title)}`}>
                <SectionIconTile $status={status} $enabled={enabled && !unavailable}>
                  {s.icon}
                </SectionIconTile>
                <SectionMeta>
                  <SectionName>{s.navTitle ?? sectionNavName(s.number, s.title)}</SectionName>
                  {unavailable ? <SectionCaption><Availability section={s} /></SectionCaption> : (s.showVerdict ?? !NO_STATUS_SECTION_NUMBERS.has(Number(s.number))) && (
                    <SectionStatusCaption $status={status} $enabled={enabled}>
                      <StatusIcon size={11} />
                      {t(`dueDiligence.finalReport.sectionHeader.status.${status}`)}
                    </SectionStatusCaption>
                  )}
                </SectionMeta>
              </SectionItemLeft>
              <SectionNumber>{String(s.displayNumber).padStart(2, "0")}</SectionNumber>
              {/*
                TODO(MIKE-305): the new design doesn't show the include/exclude-from-client-PDF
                toggle, but sales considers it a strong selling point — we'll likely bring it
                back in some form (here or in the export dialog). Hidden visually without
                unmounting the component. If it's dropped for good, delete this <Toggle> and
                everything it depends on (enabledSections, onToggleSection, the Toggle
                styled-component). If it stays, delete this comment.
              */}
              <Toggle
                $isOn={enabled}
                onClick={(e) => { e.stopPropagation(); onToggleSection(s.id); }}
                style={{ display: "none" }}
              />
            </SectionItem>
          );
        })}
      </SectionList>
    </NavWrapper>
  );
};

// ── Mobile burger variant ────────────────────────────────────────

const MobileNavWrapper = styled.div`
  padding-left: 1.5rem;
  margin-top: 0.25rem;
`;

const MobileNavHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  padding: 0.25rem 0.75rem 0.35rem;
  font-weight: 600;
`;

const MobileSectionItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: ${(p) => (p.$isActive ? 600 : 400)};
  color: ${(p) => (p.$isActive ? "#D1FAE5" : "rgba(255, 255, 255, 0.6)")};
  background: ${(p) => (p.$isActive ? "rgba(209, 250, 229, 0.1)" : "transparent")};
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  svg {
    flex-shrink: 0;
  }
`;

const MobileSectionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

const MobileSubCount = styled.span`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
  margin-right: 0.35rem;
`;

interface MobileProps {
  activeSectionId: string;
  enabledSections: Record<string, boolean>;
  onSectionClick: (sectionId: string) => void;
  onToggleSection: (sectionId: string) => void;
  sections?: ReportSectionDef[];
}

export const ReportSectionNavMobile = ({ activeSectionId, enabledSections, onSectionClick, onToggleSection, sections = [] }: MobileProps) => {
  const { t } = useTranslation();
  const activeCount = sections.filter((s) => enabledSections[s.id] !== false && !isSectionAwaitingData(s.status)).length;

  return (
    <MobileNavWrapper>
      <MobileNavHeader>
        <span>{t("dueDiligence.legalFiscalReport.reportSectionNav.sectionsHeader")}</span>
        <span>{t("dueDiligence.legalFiscalReport.reportSectionNav.activeCount", { count: activeCount })}</span>
      </MobileNavHeader>
      {sections.map((s) => {
        const enabled = enabledSections[s.id] !== false;
        return (
          <MobileSectionItem key={s.id} $isActive={activeSectionId === s.id}>
            <MobileSectionLeft onClick={() => enabled && onSectionClick(s.id)}>
              {s.icon}
              <span>{s.displayNumber}. {s.navTitle ?? sectionNavName(s.number, s.title)}</span>
            </MobileSectionLeft>
            <MobileSubCount>{isSectionAwaitingData(s.status) ? <Availability section={s} /> : `${s.subsections}/${s.subsections}`}</MobileSubCount>
            <Toggle $isOn={enabled} onClick={(e) => { e.stopPropagation(); onToggleSection(s.id); }} />
          </MobileSectionItem>
        );
      })}
    </MobileNavWrapper>
  );
};
